import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  TRAINER_SIGNUP_LINK_KEY,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { createResourceLocatorString } from '../../util/routes.js';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck.js';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { getTrainerLiveStatus, shouldShowTrainerStatusForListing } from '../../util/trainerStatus';
import { sendClaimListingEmail, cancelListingTransfer } from '../../util/api.js';
import {
  TRANSFER_STATUS_PENDING,
  TRANSFER_STATUS_CANCELLED,
  TRANSFER_STATUS_COMPLETED,
  canClaimListing,
  resolveTransferStatus,
} from '../../util/listingTransfer.js';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

import ReviewModal from '../TransactionPage/ReviewModal/ReviewModal.js';

import { clearData } from '../CheckoutPage/CheckoutPageSessionHelpers.js';
import { getTransactionTypeData } from '../CheckoutPage/CheckoutPageTransactionHelpers.js';
import { initiateInquiryWithoutPayment } from '../CheckoutPage/CheckoutPage.duck.js';

// Shared components
import {
  H2,
  H4,
  Heading,
  Page,
  NamedLink,
  NamedRedirect,
  ExternalLink,
  InlineTextButton,
  OrderPanel,
  LayoutSingleColumn,
  Modal,
  SecondaryButton,
  Reviews,
  PendingApprovalBanner,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import SectionSignupLinkMaybe from './SectionSignupLinkMaybe';
import SectionFixedPricingMaybe from './SectionFixedPricingMaybe';
import CustomListingFields from './CustomListingFields';

import instagramIcon from './instagram.svg';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { Money, UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [transferEmail, setTransferEmail] = useState();
  const [transferStatusOverride, setTransferStatusOverride] = useState();
  const [cancellingTransfer, setCancellingTransfer] = useState(false);
  const [email, setEmail] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [verifyInstaModalOpen, setVerifyInstaModalOpen] = useState(false);
  const [showInstagramModal, setShowInstagramModal] = useState(false);
  const [isReviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    onUpdateProfile,
    onInitiateInquiryWithoutPayment,
    initiateInquiryInProgress,
    initiateInquiryError,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  useEffect(() => {
    if (typeof window !== "undefined") {
      const newlyPublished = localStorage.getItem(`Published ${currentListing?.id?.uuid}`);
      const subscriptionStatus = currentListing?.attributes?.metadata?.subscriptionStatus;
      if (subscriptionStatus == "active" && newlyPublished) {
        setVerifyInstaModalOpen(true);
      };
    };
  }, [currentListing]);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const tier = metadata?.subscriptionName;
  const isClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;

  const instagramLink = `https://www.instagram.com/${publicData.instagramHandle}`;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  // The owner is previewing their own pending listing exactly as the public
  // will see it once approved. In this case we show a read-only "preview mode"
  // banner with a "Back to editing" link instead of the regular action bar.
  const isOwnerPendingPreview = isPendingApprovalVariant && isOwnListing;

  // How far this trainer is from going live. The sitewide TrainerStatusStrip
  // reads the very same helper, so the two surfaces cannot disagree. The strip
  // stands down on this page when the listing is the viewer own.
  const trainerStatus = isOwnListing ? getTrainerLiveStatus(currentListing) : null;
  const showStatusBanner = isOwnListing && shouldShowTrainerStatusForListing(currentListing);

  const hasReviewed = userAndListingAuthorAvailable && reviews.some(review => review?.author?.id?.uuid === currentUser.id.uuid);
  const isCoachReviewingCoach = userAndListingAuthorAvailable &&
    currentUser?.attributes?.profile?.publicData?.userType === 'trainer' &&
    currentListing?.author?.attributes?.profile?.publicData?.userType === 'trainer';

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  // The coach's first name for the "Train with {name}" booking button.
  // For visitors, the Sharetribe API only exposes the public displayName
  // ("Firstname L" by default), so we take its first word; profile.firstName
  // is preferred when available (e.g. the coach's own listing).
  const coachFirstName = (
    ensuredAuthor?.attributes?.profile?.firstName ||
    (authorDisplayName || '').trim().split(/\s+/)[0] ||
    ''
  ).trim();

  const priceForMeta = price || new Money(15000, config.currency);
  const { formattedPrice } = priceData(priceForMeta, config.currency, intl);

  const { linkedUserEmail } = metadata;

  const adminId = process.env.REACT_APP_ADMIN_USER_ID;
  const isAdmin = currentUser?.id?.uuid === adminId;
  const showTransfer = isAdmin && isOwnListing;

  // Local state (set by editing or cancelling in this session) takes precedence
  // over the persisted metadata value so the UI reflects changes without reload.
  const goToEmail = transferEmail !== undefined ? transferEmail : linkedUserEmail;
  const transferStatus =
    transferStatusOverride !== undefined
      ? transferStatusOverride
      : resolveTransferStatus({ metadata, isClosed });
  // `transferStatus` is the single source of truth here: the UI never guesses the
  // status from `linkedUserEmail` or from the closed state.
  const isTransferPending = transferStatus === TRANSFER_STATUS_PENDING;
  const isTransferCompleted = transferStatus === TRANSFER_STATUS_COMPLETED;
  // A new transfer can only be started when none is pending or already done.
  const showTransferButton = showTransfer && !isTransferPending && !isTransferCompleted && !isClosed;
  const showTransferInfo = showTransfer && (isTransferPending || isTransferCompleted) && goToEmail;
  // The recipient can only be changed while the transfer is still pending.
  const canEditTransfer = showTransferInfo && isTransferPending;

  const isAdminListing = ensuredAuthor?.id?.uuid == adminId;
  const currentUserType = currentUser?.attributes?.profile?.publicData?.userType;
  const maximumReviews = 5;
  const totalReviews = Number(currentUser?.attributes?.profile?.publicData?.totalReviews) || 0;
  const canNotReview = totalReviews >= maximumReviews;
  const currentUserIsntCustomer = !currentUserType || currentUserType == "trainer";
  // The public claim button may only show while no transfer is pending or
  // completed and the listing has no active paying owner (i.e. it is not closed).
  // Cancelling a transfer therefore never brings the button back on its own.
  const claimAllowedByTransfer = canClaimListing({ transferStatus, isClosed });
  const hideClaimListing = isOwnListing || !isAdminListing || !currentUserIsntCustomer || tier === 'Tier 1' || !!metadata?.originalListingId || !claimAllowedByTransfer;

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const handleSubmitTransfer = () => {
    sendClaimListingEmail({ email, listingId: rawParams.id })
      .then(() => {
        setModalOpen(false);
        setTransferEmail(email);
        setTransferStatusOverride(TRANSFER_STATUS_PENDING);
      })
      .catch(error => {
        console.error('Error sending email:', error);
      });
  };

  // Opens the transfer modal, pre-filling the input with the current recipient
  // (empty string for a brand-new transfer).
  const openTransferModal = (prefill = '') => {
    setEmail(prefill);
    setModalOpen(true);
  };

  // Clears the recipient so the listing returns to the un-transferred state.
  // Only available while the transfer is still pending.
  const handleCancelTransfer = () => {
    setCancellingTransfer(true);
    cancelListingTransfer({ listingId: rawParams.id })
      .then(() => {
        setTransferEmail('');
        setEmail('');
        setTransferStatusOverride(TRANSFER_STATUS_CANCELLED);
      })
      .catch(error => {
        console.error('Error cancelling transfer:', error);
      })
      .then(() => {
        setCancellingTransfer(false);
      });
  };

  const STORAGE_KEY = 'CheckoutPage';

  const onSubmitCallback = () => {
    clearData(STORAGE_KEY);
  };

  const onSubmitReview = values => {
    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };
      history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}, {}), state);
      return;
    }
    const { listingType, transactionProcessAlias, unitType } =
      currentListing?.attributes?.publicData || {};
    const { reviewRating, reviewContent } = values;
    const rating = Number.parseInt(reviewRating, 10);

    // const transition = transitions.REVIEWED_BY_CUSTOMER;
    const transition = "transition/reviewed-by-customer";
    const inquiryParams = {
      listingId: currentListing?.id,
      reviewRating: rating,
      reviewContent,
      protectedData: {
        listingType: listingType,
        ...getTransactionTypeData(listingType, unitType, config),
      },
    };
    onInitiateInquiryWithoutPayment(inquiryParams, transactionProcessAlias, transition)
      .then(transactionId => {
        onUpdateProfile({
          publicData: {
            totalReviews: totalReviews + 1,
          },
        });
        onSubmitCallback();
        setReviewModalOpen(false);
        setReviewSubmitted(true);
        if (typeof window !== 'undefined') {
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      })
      .catch(e => {
        // Do nothing.
      });
  };


  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
  const instagramUrl = `https://www.instagram.com/simplyfit.io/`;

  const handleRedirectClick = () => {

    if (publicData.instagramHandle && (tier === "Tier 2" || tier === "Tier 3")) {
      const cleanedHandle = publicData.instagramHandle.replace('@', '').trim();
      window.open(`https://www.instagram.com/${cleanedHandle}/`, "_blank");
    } else {
      setShowInstagramModal(true);
    }
  };

  // Navigate back to the page the user came from. If there is no previous entry
  // in the browser session history (e.g. the listing was opened directly via a
  // shared link or in a new tab), fall back to the search listing page.
  const handleBackClick = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      history.goBack();
    } else {
      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {}));
    }
  };


  // The coach's own booking link ("Train with X"). When it is missing we prompt
  // the visitor to reach out via the message button and show the contact note
  // instead (P25020-83).
  const signupUrl = publicData[TRAINER_SIGNUP_LINK_KEY];
  const hasSignupLink = !!(signupUrl || '').trim();

  // The action buttons appear in two places: stacked in the details rail on
  // desktop, and as a row pinned below the content on mobile. `layout` selects
  // which arrangement to render and where the contact note sits relative to the
  // buttons (above on desktop, below on mobile).
  const renderActions = layout => {
    const buttons = (
      <div className={layout === 'row' ? css.actionButtonsRow : css.actionButtonsStacked}>
        {hasSignupLink ? (
          <SectionSignupLinkMaybe text={signupUrl} coachFirstName={coachFirstName} />
        ) : null}
        <InlineTextButton className={css.redirect} onClick={handleRedirectClick}>
          <img src={instagramIcon} alt="Instagram" className={css.instagramIcon} />
          <FormattedMessage id="ListingPage.redirect" />
        </InlineTextButton>
      </div>
    );
    const note = !hasSignupLink ? (
      <p className={css.contactNote}>
        <FormattedMessage id="ListingPage.fixedPricing.contactNote" />
      </p>
    ) : null;
    return layout === 'stacked' ? (
      <>
        {note}
        {buttons}
      </>
    ) : (
      <>
        {buttons}
        {note}
      </>
    );
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(priceForMeta),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.carouselContent}>
          {showStatusBanner ? (
            <PendingApprovalBanner
              className={css.previewBanner}
              status={trainerStatus}
              surface="listing"
              listingParams={{ id: listingId.uuid, slug: listingSlug }}
            />
          ) : (
            <div className={css.backButtonWrapper}>
              <button type="button" className={css.backButton} onClick={handleBackClick}>
                <svg
                  className={css.backButtonIcon}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M19 12H5M5 12L12 19M5 12L12 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <FormattedMessage id="ListingPage.backButton" />
              </button>
            </div>
          )}
          <div className={css.productTopRow}>
            <div className={css.galleryColumn}>
              {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing && !isOwnerPendingPreview ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                  currentUser={currentUser}
                />
              ) : null}
              {mounted && currentListing.id && !isOwnerPendingPreview && !showStatusBanner ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  currentUser={currentUser}
                  editParams={{
                    id: listingId.uuid,
                    slug: listingSlug,
                    type: listingPathParamType,
                    tab: listingTab,
                  }}
                />
              ) : null}

              <SectionGallery
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
                currentUser={currentUser}
                currentListing={currentListing}
                onUpdateProfile={onUpdateProfile}
                onManageDisableScrolling={onManageDisableScrolling}
              />

              {showTransferInfo ? <div style={{
                fontSize: '16px',
                color: 'var(--colorAttention)',
                marginBottom: '24px',
              }}>

                <FormattedMessage
                  id={
                    isTransferCompleted
                      ? 'ListingPage.transferredRecipient'
                      : 'ListingPage.transferRecipient'
                  }
                  values={{ transferRecipient: goToEmail }}
                />
                {canEditTransfer ? <button
                  type="button"
                  onClick={() => openTransferModal(goToEmail)}
                  className={css.editTransferRecipient}
                >
                  <FormattedMessage id="ListingPage.editTransferRecipient" />
                </button> : null}
                {canEditTransfer ? <button
                  type="button"
                  onClick={handleCancelTransfer}
                  disabled={cancellingTransfer}
                  className={css.editTransferRecipient}
                >
                  <FormattedMessage id={cancellingTransfer ? 'ListingPage.cancellingTransfer' : 'ListingPage.cancelTransfer'} />
                </button> : null}
              </div> : null}

              {showTransferButton ? <button
                onClick={() => openTransferModal('')}
                className={css.openClaimModal}
              >
                <FormattedMessage id="ListingPage.listingTransfer" />
              </button> : null}
            </div>

            <div className={css.detailsColumn}>
              <div className={css.detailsHeader}>
                <div className={css.titleWithPrice}>
                  <H4 as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H4>
                  {/* Mobile only: price sits on the same row as the title. The
                      full-size price (desktop) renders separately below via
                      CustomListingFields' desktopPriceBlock. */}
                  <div className={css.mobilePriceInline}>
                    <SectionFixedPricingMaybe
                      publicData={publicData}
                      marketplaceCurrency={config.currency}
                      intl={intl}
                    />
                  </div>
                </div>
              </div>

              <CustomListingFields
                slot="details"
                publicData={publicData}
                metadata={metadata}
                description={description}
                coachFirstName={coachFirstName}
                marketplaceCurrency={config.currency}
                listingFieldConfigs={listingConfig.listingFields}
                categoryConfiguration={config.categoryConfiguration}
                intl={intl}
                isAdminListing={isAdminListing}
              />

              <div className={css.desktopActions}>{renderActions('stacked')}</div>

              {/* On desktop the social links sit in the rail below the buttons;
                  on smaller screens they render in the content flow instead. */}
              <div className={css.railLinks}>
                <CustomListingFields
                  slot="links"
                  publicData={publicData}
                  metadata={metadata}
                  coachFirstName={coachFirstName}
                  listingFieldConfigs={listingConfig.listingFields}
                  categoryConfiguration={config.categoryConfiguration}
                  intl={intl}
                  isAdminListing={isAdminListing}
                />
              </div>
            </div>
          </div>

          <div className={css.productBelow}>
            <CustomListingFields
              slot="tags"
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
              isAdminListing={isAdminListing}
            />

            {/* Buttons sit under the pills and above the Bio card on mobile
                (client feedback: a positioning request, not a sticky bar).
                Desktop already shows them in the rail above, via .desktopActions. */}
            <div className={css.mobileActions}>{renderActions('row')}</div>

            <CustomListingFields
              slot="bio"
              publicData={publicData}
              metadata={metadata}
              description={description}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
              isAdminListing={isAdminListing}
            />

            <div className={css.contentLinks}>
              <CustomListingFields
                slot="links"
                publicData={publicData}
                metadata={metadata}
                coachFirstName={coachFirstName}
                listingFieldConfigs={listingConfig.listingFields}
                categoryConfiguration={config.categoryConfiguration}
                intl={intl}
                isAdminListing={isAdminListing}
              />
            </div>

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />

            <section className={css.card}>
              <div className={css.reviewsHeader}>
                <Heading as="h2" rootClassName={css.cardHeading}>
                  <FormattedMessage id="ListingPage.reviewsTitle" values={{ count: reviews.length }} />
                </Heading>
                <div className={css.reviewsHeaderActions}>
                  <SecondaryButton
                    className={css.reviewButton}
                    onClick={() => setReviewModalOpen(true)}
                    disabled={isOwnListing || canNotReview || hasReviewed || isCoachReviewingCoach}
                  >Leave a review</SecondaryButton>
                </div>
              </div>

              {isOwnListing ? <div className={css.messageInfo}>
                This is your own listing.
              </div>
                : isCoachReviewingCoach ? <div className={css.messageInfo}>
                  Coaches can’t leave reviews for other coaches.
                </div>
                  : canNotReview ? <div className={css.messageInfo}>
                    You’ve reached your review limit of 5.
                  </div> : hasReviewed ? <div className={css.messageInfo}>
                    You’ve already submitted a review.
                  </div> : null}

              {fetchReviewsError ? (
                <H2 className={css.errorText}>
                  <FormattedMessage id="ListingPage.reviewsError" />
                </H2>
              ) : null}
              <Reviews reviews={reviews} />
            </section>

            {hideClaimListing ? null : <>
              <div className={css.claimDesc}>
                <FormattedMessage id="ListingPage.claimListingText1" />
                <br />
                <FormattedMessage id="ListingPage.claimListingText2" />
                <br />
                <FormattedMessage id="ListingPage.claimListingText3" values={{ instagramUrl: <a target='_blank' href={instagramUrl}>Instagram</a> }} />
              </div>
              <NamedLink
                name="CMSPage"
                params={{
                  pageId: "claim-listing-payment-info",
                }}
                className={css.claimListing}
              >
                <FormattedMessage id="ListingPage.claimListingButton" />
              </NamedLink>
            </>}
          </div>

          {/* <div className={css.orderColumnForProductLayout}>
            <OrderPanel
              className={css.productOrderPanel}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
            />
          </div> */}
          {console.log("currentListing", currentListing)}
          <ReviewModal
            id="ReviewOrderModal"
            isOpen={isReviewModalOpen}
            onCloseModal={() => setReviewModalOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            onSubmitReview={onSubmitReview}
            // revieweeName={otherUserDisplayName}
            reviewSent={reviewSubmitted}
            sendReviewInProgress={initiateInquiryInProgress}
            sendReviewError={initiateInquiryError}
            marketplaceName={config.marketplaceName}
            authorDisplayName={currentListing?.attributes.title || authorDisplayName}
          />

          <Modal
            onManageDisableScrolling={onManageDisableScrolling}
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
          >
            <h4 className={css.claimModalTitle}>
              <FormattedMessage id="ListingPage.transferExplaination" />
            </h4>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="enter email"
              className={css.claimModalInput}
            />
            <button
              onClick={() => {
                if (email) {
                  handleSubmitTransfer();
                };
              }}
              disabled={!email}
              className={css.claimListingButton}
            >
              <FormattedMessage id="ListingPage.transferButton" />
            </button>
          </Modal>

          <Modal
            onManageDisableScrolling={onManageDisableScrolling}
            isOpen={verifyInstaModalOpen}
            onClose={() => {
              localStorage.removeItem(`Published ${currentListing?.id?.uuid}`);
              setVerifyInstaModalOpen(false);
            }}
          >
            <h4 className={css.claimModalTitle}>
              <FormattedMessage id="ListingPage.VerifyIG.title" />
            </h4>
            <p>
              <FormattedMessage id="ListingPage.VerifyIG.text" />
            </p>
            <ExternalLink href={instagramUrl} className={css.verifyIgButton}>
              <FormattedMessage id="ListingPage.VerifyIG.link" />
            </ExternalLink>
          </Modal>

          <Modal
            isOpen={showInstagramModal}
            onClose={() => setShowInstagramModal(false)}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.modalTitle}>
              <FormattedMessage id="ListingPage.modalTitle" />
            </div>
            <p>
              {metadata.subscriptionName === "Tier 2" || metadata.subscriptionName === "Tier 3"
                ?
                <FormattedMessage id="ListingPage.instagramMissing" />
                :
                <>
                  <FormattedMessage id="ListingPage.notAvailable" />
                  <br />
                  <FormattedMessage id="ListingPage.forMoreDetails" />
                </>
              }
            </p>
            <ExternalLink
              href={instagramUrl}
            >
              <FormattedMessage id="ListingPage.openInstagram" />
            </ExternalLink>
          </Modal>

        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const { initiateInquiryInProgress, initiateInquiryError } = state.CheckoutPage;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    initiateInquiryInProgress,
    initiateInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  onUpdateProfile: params => dispatch(updateProfile(params)),
  onInitiateInquiryWithoutPayment: (inquiryParams, transactionProcessAlias, transition) =>
    dispatch(initiateInquiryWithoutPayment(inquiryParams, transactionProcessAlias, transition))
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
